/** @format */

import { DictType } from 'core-lib/types/Helper';

import { EventTargets } from 'core-lib/constants/events';
import { getDefaultAttributes, trackCustomGTMEvent, trackEvent } from 'core-lib/utils/events';
import {
  APPLY_PROMO_CLICKED,
  BOTTOM_OFFER_CARD_CLICKED,
  BOTTOM_OFFER_CARD_VIEWED,
  CHANGE_PAYMENT_METHOD,
  ORDER_CONFIRMATION_PAGE_LOADED,
  ORDER_DETAIL_PAGE_LOADED,
  PAY_NOW_CLICKED,
} from '../constants/event';
import { OrderDetailsType } from '../types/types';
import { getDeviceId } from 'core-lib/utils/source';
import { VENDOR_ID_SKU } from 'core-lib/constants/sourceSKU';
import { getPaymentLocalData } from './common';
import { SessionStorage } from 'core-lib/constants/storage';

export function firePayNowClickedEvent({
  vendorId,
  paymentMethod,
  amount,
}: {
  vendorId: string | number;
  paymentMethod: string;
  amount: number;
}) {
  trackEvent({
    name: PAY_NOW_CLICKED.name,
    defaultAttr: {},
    attributes: {
      [PAY_NOW_CLICKED.attributes.categoryId]: vendorId,
      [PAY_NOW_CLICKED.attributes.paymentMethod]: paymentMethod,
      [PAY_NOW_CLICKED.attributes.amount]: amount,
    },
  });
}

export function fireChangePaymentEvent({
  vendorId,
  paymentMethod,
}: {
  vendorId: string | number;
  paymentMethod: string;
}) {
  trackEvent({
    name: CHANGE_PAYMENT_METHOD.name,
    defaultAttr: {},
    attributes: {
      [CHANGE_PAYMENT_METHOD.attributes.categoryId]: vendorId,
      [CHANGE_PAYMENT_METHOD.attributes.paymentMethod]: paymentMethod,
    },
  });
}

export function fireApplyPromoEvent({
  vendorId,
  originSource,
}: {
  vendorId: string | number;
  originSource: string;
}) {
  trackEvent({
    name: APPLY_PROMO_CLICKED.name,
    defaultAttr: {},
    attributes: {
      [APPLY_PROMO_CLICKED.attributes.categoryId]: vendorId,
      [APPLY_PROMO_CLICKED.attributes.originSource]: originSource,
    },
  });
}

export function fireCreateOrderInitiatedEvent(vendorId?: string | number) {
  trackEvent({
    name: 'payment_flow',
    attributes: { category_id: vendorId, step: 'create_order_initiated' },
  });
}

export function fireCreateOrderCompletedEvent(vendorId?: string | number) {
  trackEvent({
    name: 'payment_flow',
    attributes: { category_id: vendorId, step: 'create_order_completed' },
  });
}

export function fireTxnPageLoaded(vendorId?: string | number) {
  trackEvent({
    name: 'payment_flow',
    attributes: { category_id: vendorId, step: 'transaction_page_loaded' },
  });
}

export function fireRedirectionToPostPayment(isFailed, vendorId?: string | number) {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: vendorId,
      step: 'redirection_to_post_payment',
      isFailed: String(isFailed),
    },
  });
}

export function fireTimerPageLoaded() {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'timer_page_loaded',
    },
  });
}

export function fireRedirectedFromTxnPage() {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'redirected_from_transaction_page',
    },
  });
}

export function fireNoDataInLocalStorage() {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'no_data_in_local_storage',
    },
  });
}

export function fireTimerPollingApiFailed() {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'timer_polling_api_failed',
    },
  });
}

export function fireRewardTimerPollingApiFailed() {
  trackEvent({
    name: 'reward_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'reward_timer_polling_api_failed',
    },
  });
}

export function fireErrorCancellingOrder() {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'error_canceling_order',
    },
  });
}

export function fireSuccessSplashLoaded() {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'success_splash_loaded',
    },
  });
}

export function fireSeeAllPaymentMethod({ paymentMethod, category }) {
  trackEvent({
    name: 'cta_clicked',
    defaultAttr: getDefaultAttributes(),
    attributes: {
      option_selected: paymentMethod,
      category_id: category,
      section: 'payment_strip',
      cta_name: 'see_all_payment_method',
    },
  });
}

export function firePaymentMethodListFetched({
  upiApps,
  vendorId,
}: {
  upiApps?: { method_name?: string }[];
  vendorId?: string | number;
}) {
  const upiNames = (upiApps ?? []).map(app => app?.method_name).filter(Boolean);
  trackEvent({
    name: 'popup_viewed',
    defaultAttr: getDefaultAttributes(),
    attributes: {
      category_id: vendorId,
      popup_name: 'installed_upi_apps',
      ...(upiNames.length ? { option: upiNames.join(',') } : {}),
    },
  });
}

export function firePollingMaxErrCount() {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'polling_max_err_count',
    },
  });
}

export function fireInvalidPayloadToInvokePSP(attr = {}) {
  trackEvent({
    name: 'payment_flow',
    attributes: { step: 'invalid_payload', ...attr },
  });
}

export function firePSPInvoked(attr: DictType = {}) {
  trackEvent({
    name: 'payment_flow',
    attributes: { step: 'psp_page_invoked', ...attr },
  });
}

export function fireCallbackPageLoaded(attr = {}) {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'callback_page_loaded',
      ...attr,
    },
  });
}

export function fireCallbackFromNativeUPI(attr = {}) {
  trackEvent({
    name: 'payment_flow',
    attributes: {
      category_id: VENDOR_ID_SKU[getPaymentLocalData()?.source]?.vendorId,
      step: 'callback_from_native_upi',
      ...attr,
    },
  });
}

export function fireOrderDetailPageLoadedEvent(order: OrderDetailsType, phoneNumber = null) {
  const attributes = {
    [ORDER_DETAIL_PAGE_LOADED.attributes.categoryId]: order.orderDetails.categoryId,
    [ORDER_DETAIL_PAGE_LOADED.attributes.orderId]: order.orderID,
    [ORDER_DETAIL_PAGE_LOADED.attributes.orderStatus]: order.orderDetails.orderStatus,
  };
  trackEvent({
    name: ORDER_DETAIL_PAGE_LOADED.name,
    attributes,
    eventTarget: [EventTargets.Appsflyer],
  });

  trackCustomGTMEvent({
    eventName: ORDER_DETAIL_PAGE_LOADED.name,
    attributes: {
      category_id: order.orderDetails.categoryId,
      cart_value: order?.orderDetails?.totalPayableAmount,
    },
  });
}

export function fireOrderConfirmationPageLoadedEvent(order: OrderDetailsType, phoneNumber = null) {
  const productType = order?.itemDetails?.map(item => item?.meta_info?.product_type)?.join(',');
  const attributes = {
    [ORDER_CONFIRMATION_PAGE_LOADED.attributes.categoryId]: order.orderDetails.categoryId,
    [ORDER_CONFIRMATION_PAGE_LOADED.attributes.orderId]: order.orderID?.toString(),
    [ORDER_CONFIRMATION_PAGE_LOADED.attributes.promoSource]: order.promoSource,
    [ORDER_CONFIRMATION_PAGE_LOADED.attributes.orderStatus]: order.orderDetails.orderStatus,
    [ORDER_CONFIRMATION_PAGE_LOADED.attributes.productType]: productType,
    [ORDER_CONFIRMATION_PAGE_LOADED.attributes.totalPrice]:
      order?.orderDetails?.totalPayableAmount?.toString(),
    [ORDER_CONFIRMATION_PAGE_LOADED.attributes.cartItems]: order?.itemDetails
      ?.map(item => item?.meta_info?.product_id)
      ?.toString(),
  };
  trackEvent({
    name: ORDER_CONFIRMATION_PAGE_LOADED.name,
    attributes,
  });

  trackCustomGTMEvent({
    eventName: `pm_${order.orderDetails.categoryId}_order_confirmed`,
    attributes: {
      category_id: order.orderDetails.categoryId,
      device_id: getDeviceId(),
      cart_value: order?.orderDetails?.totalPayableAmount,
    },
  });
}

export function fireBottomBannerCardClickEvent(
  categoryId,
  pageName,
  screenId,
  redirectionUrl,
  position,
  section,
) {
  trackEvent({
    name: BOTTOM_OFFER_CARD_CLICKED.name,
    defaultAttr: {},
    attributes: {
      [BOTTOM_OFFER_CARD_CLICKED.attributes.categoryId]: categoryId,
      [BOTTOM_OFFER_CARD_CLICKED.attributes.pageName]: pageName,
      [BOTTOM_OFFER_CARD_CLICKED.attributes.screenId]: screenId,
      [BOTTOM_OFFER_CARD_CLICKED.attributes.redirectionUrl]: redirectionUrl,
      [BOTTOM_OFFER_CARD_CLICKED.attributes.position]: position,
      [BOTTOM_OFFER_CARD_CLICKED.attributes.section]: section,
    },
  });
}

export function fireBottomBannerCardViewedEvent(
  categoryId,
  pageName,
  screenId,
  redirectionUrl,
  position,
  section,
) {
  trackEvent({
    name: BOTTOM_OFFER_CARD_VIEWED.name,
    defaultAttr: {},
    attributes: {
      [BOTTOM_OFFER_CARD_VIEWED.attributes.categoryId]: categoryId,
      [BOTTOM_OFFER_CARD_VIEWED.attributes.pageName]: pageName,
      [BOTTOM_OFFER_CARD_VIEWED.attributes.screenId]: screenId,
      [BOTTOM_OFFER_CARD_VIEWED.attributes.redirectionUrl]: redirectionUrl,
      [BOTTOM_OFFER_CARD_VIEWED.attributes.position]: position,
      [BOTTOM_OFFER_CARD_VIEWED.attributes.section]: section,
    },
  });
}

export const trackCtaClicked = ({
  option = '',
  section = '',
  ctaName = '',
  vendorId,
}: {
  option: string;
  section: string;
  ctaName?: string;
  vendorId?: string | number;
}) => {
  trackEvent({
    name: 'cta_clicked',
    defaultAttr: getDefaultAttributes(),
    attributes: {
      category_id: vendorId,
      option,
      section,
      cta_name: ctaName,
    },
  });
};
